import { useCallback, useEffect, useState } from 'react';
import { getApiUrl } from '../../config';
import { loadToken, storeToken, clearToken, readTokenFromHash } from './counterToken';

export interface CounterVenue { id: string; name: string; address: string }
export interface CounterMatch {
  id: string;
  location: string;
  date: string;
  time: string;
  organizerName: string | null;
  playersCount: number;
  registeredPlayersCount: number;
}

export function useCounterSession() {
  const [token, setToken] = useState<string | null>(null);
  const [venue, setVenue] = useState<CounterVenue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // El token entra por el link una sola vez. Se guarda y se saca del hash
    // enseguida: el celular queda sobre un mostrador, a la vista de cualquiera.
    const fromHash = readTokenFromHash(window.location.hash);
    if (fromHash) {
      storeToken(fromHash);
      window.history.replaceState(null, '', window.location.pathname);
    }
    setToken(fromHash || loadToken());
  }, []);

  const request = useCallback(
    async (path: string, options: { method?: string; body?: any } = {}) => {
      const current = loadToken();
      if (!current) throw new Error('NO_TOKEN');
      const res = await fetch(getApiUrl(`/counter${path}`), {
        method: options.method || 'GET',
        headers: { 'Content-Type': 'application/json', 'X-Counter-Token': current },
        body: options.body ? JSON.stringify(options.body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err: any = new Error(data.error || 'Error');
        err.code = data.code;
        err.status = res.status;
        throw err;
      }
      return data;
    },
    []
  );

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const me = await request('/me');
        if (cancelled) return;
        setVenue(me.venue);
        // Activar la sesión es idempotente: el server responde alreadyClaimed.
        await request('/claim', { method: 'POST' });
      } catch (err: any) {
        if (cancelled) return;
        setError(err.message || 'No pudimos abrir la sesión');
        if (err.status === 401) clearToken();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, request]);

  return { token, venue, loading, error, request };
}
